import { useCallback, useMemo } from "react";

import {
  ChefGroupingRead,
  useGetChefGroupingQuery,
  useGetProductGroupingQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "./settings";

export interface DefaultTargetBasketRow {
  id: number;
  name: string;
  productGroupId: number;
  productGroupName: string;
  steelGrades: string[];
  defaultTargetNumBaskets: number;
}

export const useDefaultTargetBaskets = () => {
  const tenantName = useTenant();
  const customerQuery = useGetChefGroupingQuery({
    tenantName,
    chefGroupingId: "default",
    source: "customer",
  });
  const scrapchefQuery = useGetChefGroupingQuery({
    tenantName,
    chefGroupingId: "default",
    source: "scrapchef",
  });

  const activeGrouping: ChefGroupingRead | undefined =
    customerQuery.data ?? scrapchefQuery.data;

  const productGroupingQuery = useGetProductGroupingQuery(
    activeGrouping
      ? { tenantName, productGroupingId: activeGrouping.product_grouping_id }
      : { tenantName, productGroupingId: "default" }
  );

  const productGroupsById = useMemo(() => {
    if (!productGroupingQuery.data) {
      return {};
    }
    return productGroupingQuery.data.product_groups
      .filter((pg): pg is typeof pg & { id: number } => pg.id != null)
      .reduce<Record<number, string>>((acc, pg) => {
        acc[pg.id] = pg.name;
        return acc;
      }, {});
  }, [productGroupingQuery.data]);

  const rows: DefaultTargetBasketRow[] = useMemo(() => {
    if (!activeGrouping) {
      return [];
    }

    return activeGrouping.chef_groups
      .filter(
        (group): group is typeof group & { id: number } => group.id != null
      )
      .map((group) => ({
        id: group.id,
        name: group.name,
        productGroupId: group.product_group_id,
        productGroupName: productGroupsById[group.product_group_id] ?? "",
        steelGrades: group.steel_grades.map((grade) => grade.name),
        defaultTargetNumBaskets: group.default_target_num_baskets,
      }));
  }, [activeGrouping, productGroupsById]);

  // Key by group name since IDs differ between customer and scrapchef groupings
  const scrapchefDefaultsByName: Record<string, number> = useMemo(() => {
    if (!scrapchefQuery.data) {
      return {};
    }
    return scrapchefQuery.data.chef_groups.reduce<Record<string, number>>(
      (acc, group) => {
        acc[group.name] = group.default_target_num_baskets;
        return acc;
      },
      {}
    );
  }, [scrapchefQuery.data]);

  const showScrapchefWarning = useMemo(() => {
    // Only show warning if we have a customer default and a different scrapchef default
    const hasCustomerDefault = customerQuery.data?.source === "customer";
    const hasScrapchefDefault = !!scrapchefQuery.data;

    if (!hasCustomerDefault || !hasScrapchefDefault) {
      return false;
    }

    // Only show warning if scrapchef default is newer than customer default
    const scrapCreated = Date.parse(scrapchefQuery.data?.created_at ?? "");
    const customerCreated = Date.parse(customerQuery.data?.created_at ?? "");
    const isScrapchefNewer =
      Number.isFinite(scrapCreated) &&
      Number.isFinite(customerCreated) &&
      scrapCreated > customerCreated;

    if (!isScrapchefNewer) {
      return false;
    }

    // Check if the target basket values are actually different
    return (
      customerQuery.data?.chef_groups.some((customerGroup) => {
        const scrapGroup = scrapchefQuery.data?.chef_groups.find(
          (g) => g.name === customerGroup.name
        );
        return (
          scrapGroup?.default_target_num_baskets !==
          customerGroup.default_target_num_baskets
        );
      }) ?? false
    );
  }, [customerQuery.data, scrapchefQuery.data]);

  const refetch = useCallback(async () => {
    await Promise.all([customerQuery.refetch(), scrapchefQuery.refetch()]);
  }, [customerQuery, scrapchefQuery]);

  return {
    data: activeGrouping,
    rows,
    isLoading:
      customerQuery.isLoading ||
      scrapchefQuery.isLoading ||
      productGroupingQuery.isLoading,
    isFetching:
      customerQuery.isFetching ||
      scrapchefQuery.isFetching ||
      productGroupingQuery.isFetching,
    isError: customerQuery.isError && scrapchefQuery.isError,
    refetch,
    showScrapchefWarning,
    hasCustomerDefault: customerQuery.data?.source === "customer",
    scrapchefDefaultsByName,
  };
};
